/**
 * CalendarView — Aurora-style obligations + events calendar.
 *
 * Source of truth: GET /api/contracts/calendar — server enriches every item
 * with `due_date`, `days_until`, `status_computed` (overdue | due_soon |
 * upcoming | met), and the parent contract's project + asset context.
 *
 * Layout
 * ──────
 *   • Top bar: month navigation · status filter chips · counts
 *   • Two-column body:
 *       Left  (8/12): month grid with colour-coded event chips per day
 *       Right (4/12): "Upcoming" list — next 30 days, sortable, clickable
 *   • Event click → side-drawer with detail + Mark Complete action
 *
 * Visual language matches Aurora dashboards: clean cards, dotted today
 * indicator, status colours (red overdue · amber due-soon · green met ·
 * grey upcoming).
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Stack, Card, Typography, Chip, IconButton, Tooltip,
  CircularProgress, Alert, Drawer, Button, Divider, Select, MenuItem,
} from '@mui/material';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EventNoteIcon from '@mui/icons-material/EventNote';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorBoundary    from './ErrorBoundary';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  overdue:  { bg: 'rgba(229, 56, 79, 0.16)',  fg: '#e5384f', label: 'Overdue'  },
  due_soon: { bg: 'rgba(212, 147, 13, 0.18)', fg: '#d4930d', label: 'Due soon' },
  upcoming: { bg: 'rgba(99, 102, 241, 0.14)', fg: '#6366f1', label: 'Upcoming' },
  met:      { bg: 'rgba(217, 138, 43, 0.14)',  fg: '#D98A2B', label: 'Met'      },
};

const KIND_LABEL = { obligation: 'Recurring obligation', event: 'Dated event' };

// Sentinels for the team filter — real values are team uuids.
const TEAM_ALL = '__all__';
const TEAM_UNASSIGNED = '__unassigned__';

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'overdue',  label: 'Overdue' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'upcoming', label: 'Upcoming' },
];

const fmtAud = (n) => (n == null ? null : `A$${Math.round(n).toLocaleString('en-AU')}`);
const fmtDay = (d) => (d ? dayjs(d).format('D MMM') : '—');

// ─── Subcomponents ───────────────────────────────────────────────────────────

/**
 * One day cell in the month grid. Stacks up to MAX_VISIBLE event chips
 * with a "+N more" overflow indicator. Clicking a chip opens the detail
 * drawer; clicking the day number itself toggles a per-day view (TODO).
 */
const DAY_CHIP_MAX = 3;

function DayCell({ date, items, isCurrentMonth, isToday, onItemClick }) {
  return (
    <Box sx={{
      minHeight: 96,
      p: 0.75,
      bgcolor: isToday ? 'rgba(217, 138, 43, 0.06)' : 'background.paper',
      border: '1px solid', borderColor: 'divider',
      borderRadius: 1,
      opacity: isCurrentMonth ? 1 : 0.42,
      display: 'flex', flexDirection: 'column',
      gap: 0.5,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{
          fontSize: '0.78rem',
          fontWeight: isToday ? 700 : 500,
          color: isToday ? 'primary.main' : isCurrentMonth ? 'text.primary' : 'text.disabled',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {date.format('D')}
        </Typography>
        {isToday && (
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: 'primary.main',
          }} />
        )}
      </Stack>

      {items.slice(0, DAY_CHIP_MAX).map((item, i) => {
        const s = STATUS_STYLE[item.status_computed] || STATUS_STYLE.upcoming;
        return (
          <Tooltip key={i} title={`${item.label} · ${item.counterparty}`} placement="top">
            <Box
              onClick={() => onItemClick(item)}
              sx={{
                cursor: 'pointer',
                px: 0.75, py: 0.25,
                borderRadius: 0.5,
                bgcolor: s.bg,
                color:   s.fg,
                fontSize: '0.65rem',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                borderLeft: '2px solid', borderLeftColor: s.fg,
                '&:hover': { filter: 'brightness(1.1)' },
              }}
            >
              {item.label}
            </Box>
          </Tooltip>
        );
      })}

      {items.length > DAY_CHIP_MAX && (
        <Typography sx={{
          fontSize: '0.65rem', color: 'text.secondary', mt: 'auto',
          fontWeight: 600,
        }}>
          +{items.length - DAY_CHIP_MAX} more
        </Typography>
      )}
    </Box>
  );
}

/**
 * Compact row in the "Upcoming" side panel. Shows the date pill, title,
 * counterparty/context, and status.
 */
function UpcomingRow({ item, onClick }) {
  const s = STATUS_STYLE[item.status_computed] || STATUS_STYLE.upcoming;
  const d = item.due_date ? dayjs(item.due_date) : null;

  return (
    <Box
      onClick={() => onClick(item)}
      sx={{
        cursor: 'pointer',
        display: 'flex', alignItems: 'flex-start', gap: 1.25,
        p: 1.25,
        borderRadius: 1,
        borderLeft: '3px solid', borderLeftColor: s.fg,
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background 0.15s ease',
      }}>
      {/* Date stamp */}
      <Box sx={{ minWidth: 44, textAlign: 'center' }}>
        <Typography sx={{
          fontSize: '0.7rem',
          color: s.fg,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 700,
        }}>
          {d ? d.format('MMM') : '—'}
        </Typography>
        <Typography sx={{
          fontSize: '1.05rem',
          fontWeight: 800,
          lineHeight: 1.2,
          color: 'text.primary',
        }}>
          {d ? d.format('D') : '—'}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.85rem',
          fontWeight: 600,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.label}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
          {item.kind === 'obligation'
            ? <RepeatRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            : <EventNoteIcon sx={{ fontSize: 12, color: 'text.secondary' }} />}
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {item.counterparty}
            {item.cadence && ` · ${item.cadence}`}
          </Typography>
          {/* Owning team, so the filter's effect is visible on the row itself.
              Unassigned items say so rather than showing nothing — silence
              would read as "no team column" instead of "needs an owner". */}
          <Chip
            label={item.team_name || 'Unassigned'}
            size="small"
            sx={{
              height: 15, fontSize: '0.6rem', ml: 0.25,
              bgcolor: item.team_name ? 'rgba(217, 138, 43, 0.14)' : 'transparent',
              color:   item.team_name ? '#D98A2B' : 'text.disabled',
              border: item.team_name ? 'none' : '1px dashed',
              borderColor: 'divider',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        </Stack>
      </Box>

      {/* Status / days-until */}
      <Stack alignItems="flex-end" spacing={0.25}>
        <Chip
          label={s.label}
          size="small"
          sx={{
            bgcolor: s.bg, color: s.fg,
            fontSize: '0.65rem', height: 18,
            fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        />
        {item.days_until != null && (
          <Typography sx={{
            fontSize: '0.65rem',
            color: 'text.secondary',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {item.days_until < 0
              ? `${Math.abs(item.days_until)}d ago`
              : item.days_until === 0 ? 'today'
              : `${item.days_until}d`}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function CalendarView() {
  const navigate = useNavigate();
  const { portfolioId } = useParams();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [month, setMonth]       = useState(dayjs());
  const [filter, setFilter]     = useState('all');
  const [teams, setTeams]       = useState([]);
  const [teamFilter, setTeamFilter] = useState(TEAM_ALL);
  const [selected, setSelected] = useState(null);    // detail-drawer item

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Scoped to this portfolio so the calendar agrees with the Contracts
      // list in the same workspace.
      const r = await axios.get(`${API_BASE_URL}/api/contracts/calendar`,
                                { params: { portfolio_id: portfolioId } });
      setItems(r.data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);
  useEffect(() => { load(); }, [load]);

  // Teams for the ownership filter. Non-fatal on failure — the filter just
  // offers Everyone / Unassigned, and items still show the team name the
  // server sent.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/teams`)
      .then((r) => { if (!cancelled) setTeams(r.data.teams || []); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Filter + group items ───────────────────────────────────────────────────
  // Team filter applies first so the status chip counts describe the team
  // you're looking at, not the whole org.
  const teamScoped = useMemo(() => {
    if (teamFilter === TEAM_ALL) return items;
    if (teamFilter === TEAM_UNASSIGNED) return items.filter(i => !i.team_id);
    return items.filter(i => i.team_id === teamFilter);
  }, [items, teamFilter]);

  const filtered = useMemo(() => {
    if (filter === 'all') return teamScoped;
    return teamScoped.filter(i => i.status_computed === filter);
  }, [teamScoped, filter]);

  // Counts per status — for the filter-chip badges, within the chosen team
  const counts = useMemo(() => {
    const c = { all: teamScoped.length, overdue: 0, due_soon: 0, upcoming: 0, met: 0 };
    for (const i of teamScoped) {
      const s = i.status_computed;
      if (c[s] != null) c[s] += 1;
    }
    return c;
  }, [teamScoped]);

  // Totals per team for the dropdown, over the unfiltered set so the numbers
  // don't move as you change the status filter.
  const teamCounts = useMemo(() => {
    const c = { [TEAM_ALL]: items.length, [TEAM_UNASSIGNED]: 0 };
    for (const i of items) {
      if (!i.team_id) { c[TEAM_UNASSIGNED] += 1; continue; }
      c[i.team_id] = (c[i.team_id] || 0) + 1;
    }
    return c;
  }, [items]);

  // Index items by ISO date for month-grid lookup
  const byDate = useMemo(() => {
    const m = {};
    for (const i of filtered) {
      if (!i.due_date) continue;
      (m[i.due_date] ||= []).push(i);
    }
    return m;
  }, [filtered]);

  // Upcoming list — next 30 days only, sorted ascending. Overdues bubble up
  // to the top regardless of date so they stay visible.
  const upcoming = useMemo(() => {
    const cutoff = dayjs().add(30, 'day');
    return filtered
      .filter(i => i.due_date && (
        i.status_computed === 'overdue'
        || dayjs(i.due_date).isBefore(cutoff)
      ))
      .sort((a, b) => {
        // Overdue first, then by date ascending
        if (a.status_computed === 'overdue' && b.status_computed !== 'overdue') return -1;
        if (b.status_computed === 'overdue' && a.status_computed !== 'overdue') return 1;
        return (a.due_date || '').localeCompare(b.due_date || '');
      })
      .slice(0, 20);
  }, [filtered]);

  // ── Build month grid ───────────────────────────────────────────────────────
  // Always render 6 weeks × 7 days so the grid height is stable across months.
  const grid = useMemo(() => {
    const monthStart = month.startOf('month');
    const gridStart = monthStart.day() === 0
      ? monthStart.subtract(6, 'day')               // Sunday → back to Monday
      : monthStart.startOf('week').add(1, 'day');   // start on Monday
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = gridStart.add(i, 'day');
      days.push({
        date: d,
        isCurrentMonth: d.month() === month.month(),
        isToday: d.isSame(dayjs(), 'day'),
        items: byDate[d.format('YYYY-MM-DD')] || [],
      });
    }
    return days;
  }, [month, byDate]);

  // ── Mark obligation as fulfilled ───────────────────────────────────────────
  const markComplete = async (item) => {
    if (item.kind !== 'obligation' || !item.code || !item.contract_id) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/contracts/${item.contract_id}/obligations/${item.code}/fulfill`,
        {}
      );
      setSelected(null);
      load();    // refresh — server recomputes status + next_due
    } catch (err) {
      // toast surfaced via the page-level error banner
      setError(err.response?.data?.error || 'Failed to mark complete');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  // No outer padding — AppLayout already wraps the page with `py: 2`.
  // Adding more here caused the page to "jump" when switching tabs.
  return (
    <Box>
      {/* Top bar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Previous month">
            <IconButton size="small" onClick={() => setMonth(m => m.subtract(1, 'month'))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
            onClick={() => setMonth(dayjs())}
            sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary' }}
          >
            Today
          </Button>
          <Tooltip title="Next month">
            <IconButton size="small" onClick={() => setMonth(m => m.add(1, 'month'))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography sx={{
          fontSize: '1.25rem', fontWeight: 700,
          letterSpacing: '-0.02em', color: 'text.primary',
        }}>
          {month.format('MMMM YYYY')}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Team filter — ownership comes from contract_task_teams, surfaced on
            every calendar item as team_id / team_name. */}
        <Select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 168, mr: 1, height: 30, fontSize: '0.75rem' }}
          renderValue={(v) => {
            const n = teamCounts[v] ?? 0;
            if (v === TEAM_ALL) return `All teams · ${n}`;
            if (v === TEAM_UNASSIGNED) return `Unassigned · ${n}`;
            return `${teams.find(t => t.id === v)?.name || 'Team'} · ${n}`;
          }}
        >
          <MenuItem value={TEAM_ALL} sx={{ fontSize: '0.78rem' }}>
            All teams · {teamCounts[TEAM_ALL] ?? 0}
          </MenuItem>
          <MenuItem value={TEAM_UNASSIGNED} sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            Unassigned · {teamCounts[TEAM_UNASSIGNED] ?? 0}
          </MenuItem>
          {/* No <Divider/> here: Select stamps role="option" onto every child,
              so a divider becomes a phantom empty option that screen readers
              announce and arrow-keys land on. The labels separate the two
              groups well enough on their own. */}
          {teams.map(t => (
            <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.78rem' }}>
              {t.name} · {teamCounts[t.id] ?? 0}
            </MenuItem>
          ))}
        </Select>

        {/* Filter chips */}
        <Stack direction="row" spacing={0.75}>
          {FILTERS.map(f => (
            <Chip
              key={f.key}
              label={`${f.label}${counts[f.key] != null ? ` · ${counts[f.key]}` : ''}`}
              size="small"
              onClick={() => setFilter(f.key)}
              sx={{
                cursor: 'pointer',
                bgcolor: filter === f.key ? 'primary.main' : 'transparent',
                color:   filter === f.key ? 'background.paper' : 'text.secondary',
                border: '1px solid',
                borderColor: filter === f.key ? 'primary.main' : 'divider',
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            />
          ))}
        </Stack>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Body — 2-column on md+, stacked on xs */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2.5fr 1fr' },
        gap: 2,
      }}>
        {/* ── Month grid ─────────────────────────────────────────────────── */}
        <ErrorBoundary name="Month grid">
          <Card sx={{ p: 1.5, bgcolor: 'background.paper' }}>
            {/* Day-of-week headings */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5,
              mb: 0.5,
            }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <Typography key={d} sx={{
                  fontSize: '0.65rem', fontWeight: 700,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  textAlign: 'center',
                  py: 0.5,
                }}>
                  {d}
                </Typography>
              ))}
            </Box>

            {/* The 6×7 grid */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5,
            }}>
              {loading ? (
                Array.from({length: 42}).map((_, i) => (
                  <Box key={i} sx={{
                    minHeight: 96, borderRadius: 1,
                    bgcolor: 'action.hover',
                  }} />
                ))
              ) : (
                grid.map((cell, i) => (
                  <DayCell
                    key={i}
                    date={cell.date}
                    items={cell.items}
                    isCurrentMonth={cell.isCurrentMonth}
                    isToday={cell.isToday}
                    onItemClick={setSelected}
                  />
                ))
              )}
            </Box>
          </Card>
        </ErrorBoundary>

        {/* ── Upcoming side panel ───────────────────────────────────────── */}
        <ErrorBoundary name="Upcoming list">
          <Card sx={{ p: 1.5, bgcolor: 'background.paper', alignSelf: 'start' }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25, px: 0.5 }}>
              <Typography sx={{
                fontSize: '0.74rem', fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                Upcoming · next 30 days
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                {upcoming.length}
              </Typography>
            </Stack>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : upcoming.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 32, color: 'primary.main', opacity: 0.4 }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>
                  Nothing due in the next 30 days.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={0.75}>
                {upcoming.map((item, i) => (
                  <UpcomingRow key={`${item.contract_id}-${item.code}-${i}`}
                               item={item}
                               onClick={setSelected} />
                ))}
              </Stack>
            )}
          </Card>
        </ErrorBoundary>
      </Box>

      {/* ── Detail drawer ───────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        {selected && (
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              {selected.kind === 'obligation'
                ? <RepeatRoundedIcon sx={{ color: 'primary.main' }} />
                : <EventNoteIcon sx={{ color: 'primary.main' }} />}
              <Typography sx={{
                fontSize: '0.7rem',
                fontWeight: 700, color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                {KIND_LABEL[selected.kind] || selected.kind}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                label={(STATUS_STYLE[selected.status_computed] || STATUS_STYLE.upcoming).label}
                size="small"
                sx={{
                  bgcolor: (STATUS_STYLE[selected.status_computed] || STATUS_STYLE.upcoming).bg,
                  color:   (STATUS_STYLE[selected.status_computed] || STATUS_STYLE.upcoming).fg,
                  fontWeight: 700, fontSize: '0.7rem',
                }}
              />
            </Stack>

            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 0.75 }}>
              {selected.label}
            </Typography>

            <Stack spacing={1.25}>
              <DetailRow k="Counterparty" v={selected.counterparty} />
              <DetailRow k="Team" v={selected.team_name || 'Unassigned'} />
              {selected.project_name && <DetailRow k="Project" v={selected.project_name} />}
              <DetailRow k="Date" v={selected.due_date ? dayjs(selected.due_date).format('dddd, D MMMM YYYY') : '—'} />
              {selected.cadence && <DetailRow k="Cadence" v={selected.cadence} />}
              {selected.recipient && <DetailRow k="Recipient" v={selected.recipient} />}
              {selected.clause_ref && <DetailRow k="Clause" v={selected.clause_ref} mono />}

              {/* Consequence: rendered differently for events vs obligations */}
              {(selected.consequence || selected.consequence_aud) && (
                <Box sx={{
                  p: 1.25, borderRadius: 1,
                  bgcolor: 'rgba(229, 56, 79, 0.06)',
                  border: '1px solid rgba(229, 56, 79, 0.18)',
                }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#e5384f' }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#e5384f',
                                       textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Consequence if missed
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                    {selected.consequence?.kind === 'delay_damages'
                      ? `Delay damages ${fmtAud(selected.consequence.per_day_aud)}/day · capped at ${fmtAud(selected.consequence.cap_aud)}`
                      : selected.consequence?.kind
                        ? selected.consequence.kind.replace(/_/g, ' ')
                        : selected.consequence_aud
                          ? `Estimated cost ${fmtAud(selected.consequence_aud)}`
                          : '—'}
                  </Typography>
                </Box>
              )}

              {(selected.description || selected.notes) && (
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary',
                                     textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>
                    {selected.description || selected.notes}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Stack direction="row" spacing={1}>
              {selected.kind === 'obligation' && selected.status_computed !== 'met' && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={() => markComplete(selected)}
                >
                  Mark fulfilled
                </Button>
              )}
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setSelected(null);
                  if (portfolioId && selected.contract_id) {
                    navigate(`/portfolios/${portfolioId}/contracts/${selected.contract_id}`);
                  }
                }}
              >
                Open contract
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

const DetailRow = ({ k, v, mono = false }) => (
  <Stack direction="row" spacing={2}>
    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', minWidth: 120,
                       textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {k}
    </Typography>
    <Typography sx={{
      fontSize: '0.85rem', color: 'text.primary', flex: 1,
      ...(mono && { fontFamily: "'JetBrains Mono', monospace" }),
    }}>
      {v}
    </Typography>
  </Stack>
);
